import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { PackageCheck, FileCode, Download, FileSpreadsheet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import GinGrnSearchBar, { SEARCH_FIELDS } from "@/components/GinGrnSearchBar";
import { exportGrnsExcel } from "@/lib/procurementExports";

export default function GoodsReceiptPage() {
  const [list, setList] = useState([]);
  const [items, setItems] = useState([]);
  const [stores, setStores] = useState([]);
  const [selected, setSelected] = useState(null);
  // Active Search criteria ({ part_no } / { supplier }, or {} for all GRNs).
  const [search, setSearch] = useState({});
  const searchActive = Object.keys(search).length > 0;

  const runSearch = async (params) => {
    try {
      const { data } = await api.get("/grns", { params });
      setSearch(params);
      setList(data);
      setSelected((cur) => (cur && data.some((g) => g.id === cur.id) ? cur : null));
    } catch {
      toast.error("Search failed");
    }
  };

  // Export — a Tally "Receipt Note" voucher XML, ready for Gateway of Tally
  // -> Import Data -> Vouchers, so accepted goods receipts can be brought
  // into Tally without re-keying them.
  const exportGrnTallyXml = async (grn) => {
    try {
      const res = await api.get(`/grns/${grn.id}/tally-xml`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/xml" }));
      const a = document.createElement("a"); a.href = url; a.download = `${grn.code}_tally.xml`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      toast.success(`Exported ${grn.code}_tally.xml`);
    } catch {
      toast.error("Export failed");
    }
  };

  const downloadGrnPdf = async (grn) => {
    try {
      const res = await api.get(`/grns/${grn.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = `${grn.code}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      toast.success(`Downloaded ${grn.code}.pdf`);
    } catch {
      toast.error("PDF download failed");
    }
  };

  const itemMap = useMemo(() => Object.fromEntries(items.map((i) => [i.id, i])), [items]);
  const storeMap = useMemo(() => Object.fromEntries(stores.map((s) => [s.code, s.name])), [stores]);

  const exportList = () => {
    try {
      exportGrnsExcel({ grns: list, itemMap, storeMap });
      toast.success(`Exported ${list.length} GRN(s)`);
    } catch {
      toast.error("Export failed");
    }
  };

  useEffect(() => {
    (async () => {
      const [g, i, s] = await Promise.all([
        api.get("/grns"), api.get("/items"), api.get("/stores"),
      ]);
      setList(g.data);
      setItems(i.data);
      setStores(s.data);
    })();
  }, []);

  return (
    <div className="space-y-6" data-testid="grn-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Inward</div>
          <h1 className="text-3xl sm:text-4xl tracking-tight font-extrabold text-slate-900" style={{ fontFamily: "Manrope" }}>
            Goods Receipt Notes
          </h1>
          <p className="text-sm text-slate-500 mt-1">Auto-generated when Quality approves a Goods Inward Note. Stock has been credited to the target store.</p>
        </div>
        <Button variant="outline" onClick={exportList} disabled={list.length === 0} data-testid="grn-export-list-btn"
          title="Export the GRNs listed below (respects the active search) to Excel">
          <FileSpreadsheet className="w-4 h-4 mr-1.5" /> Export
        </Button>
      </div>

      <GinGrnSearchBar onSearch={runSearch} active={searchActive} testIdPrefix="grn" />

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 text-sm font-semibold text-slate-700 flex justify-between gap-2">
            <span className="truncate">
              {searchActive
                ? <>Search results · {SEARCH_FIELDS[Object.keys(search)[0]]}: <span className="mono">{Object.values(search)[0]}</span></>
                : "Receipts"}
            </span>
            <span className="text-xs text-slate-500 mono">{list.length}</span>
          </div>
          <div className="divide-y divide-slate-100 max-h-[70vh] overflow-y-auto scroll-thin">
            {list.length === 0 && (searchActive
              ? <div className="px-4 py-10 text-center text-sm text-slate-500" data-testid="grn-no-results">No records found.</div>
              : <div className="px-4 py-10 text-center text-sm text-slate-500" data-testid="no-grns">No goods receipt notes yet. Approve a GIN to create one.</div>)}
            {list.map((g) => (
              <button key={g.id} onClick={() => setSelected(g)} data-testid={`grn-row-${g.code}`}
                className={`w-full text-left px-4 py-3 hover:bg-slate-50 transition ${selected?.id === g.id ? "bg-blue-50/60" : ""}`}>
                <div className="flex items-center justify-between">
                  <div className="mono text-xs text-slate-500 uppercase tracking-[0.14em]">{g.code}</div>
                  <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">Received</span>
                </div>
                <div className="mt-1 text-sm font-medium text-slate-800">{g.supplier || "Manual"}</div>
                <div className="text-xs text-slate-500">
                  From <span className="mono text-blue-700">{g.gin_code}</span>{g.po_code ? ` · ${g.po_code}` : ""} · {g.lines?.length || 0} line(s)
                </div>
                {searchActive && (
                  <div className="text-[11px] text-slate-500 mono truncate">
                    {(g.lines || []).map((l) => itemMap[l.item_id]?.code || l.part_code || l.part_name).filter(Boolean).join(", ")}
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>

        <div className="lg:col-span-3 bg-white border border-slate-200 rounded-lg p-6 min-h-[60vh]" data-testid="grn-detail">
          {!selected ? (
            <div className="h-full flex flex-col items-center justify-center text-sm text-slate-500 gap-2">
              <PackageCheck className="w-10 h-10 text-slate-300" /> Select a GRN to view details.
            </div>
          ) : (
            <div className="space-y-5">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-xs uppercase tracking-[0.14em] text-slate-500">{selected.code}</div>
                  <h2 className="text-2xl font-extrabold tracking-tight text-slate-900" style={{ fontFamily: "Manrope" }}>
                    {selected.supplier || "Manual receipt"}
                  </h2>
                  <div className="text-xs text-slate-500 mt-1">
                    From <span className="mono text-blue-700">{selected.gin_code}</span>
                    {selected.po_code ? <> · PO <span className="mono">{selected.po_code}</span></> : null} · {new Date(selected.created_at).toLocaleString()}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">Inspected by {selected.inspected_by}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button size="sm" variant="outline" onClick={() => exportGrnTallyXml(selected)} data-testid="grn-export-btn"
                    title="Export as a Tally-importable Receipt Note voucher XML">
                    <FileCode className="w-4 h-4 mr-1.5" /> Export
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => downloadGrnPdf(selected)} data-testid="grn-pdf-btn">
                    <Download className="w-4 h-4 mr-1.5" /> PDF
                  </Button>
                </div>
              </div>

              <div>
                <div className="text-xs uppercase tracking-[0.14em] text-slate-500 mb-2">Accepted Lines</div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[10px] uppercase tracking-[0.14em] text-slate-500 bg-slate-50">
                      <th className="py-2 px-3">Item</th>
                      <th className="py-2 px-3">Accepted Qty</th>
                      <th className="py-2 px-3">Credited To</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selected.lines.map((l, idx) => (
                      <tr key={idx} className="border-t border-slate-100">
                        <td className="py-2 px-3 mono">{itemMap[l.item_id]?.code || l.part_code} <span className="text-slate-500 text-xs">— {itemMap[l.item_id]?.name || l.part_name}</span></td>
                        <td className="py-2 px-3 mono font-semibold text-emerald-700">+ {l.accepted_qty}</td>
                        <td className="py-2 px-3 text-xs">{l.is_manual ? "No stock (one-time item)" : (storeMap[l.target_store] || l.target_store)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="text-xs text-slate-500 flex items-center gap-1">
                <PackageCheck className="w-3.5 h-3.5 text-emerald-600" />
                Inventory credited to the listed stores.
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
